/**
 * Pure section-leader time gate for the v2 section signup data
 * model. Every SL pick or change is a move from slot A to slot B
 * (a first pick has no A, choosing "none" has no B) and must pass
 * a leave test on A and a join test on B.
 *
 * All counts are for one pool. `sections` counts countable SL
 * sections in the slot, `students` counts student picks, and
 * `remainingSeats` is `sections × capStudents − students`, as in
 * `SectionSignupV2SlotAvailability`.
 */
import {z} from 'zod';

import type {SectionSignupV2SlotAvailability} from './sectionSignupV2Availability';
import {SECTION_SIGNUP_V2_MEAN_SHARE_BY_SLOT} from './sectionSignupV2MeanShare';
import type {SectionSignupV2SlSlotCaps} from './sectionSignupV2Meta';

/** Why the gate refused a move. */
export const SlGateRefusalSchema = z.enum([
  'leave-over-max',
  'leave-last-sl',
  'join-capped',
  'join-under-min',
]);

/**
 * - `leave-over-max`: the students left behind would exceed
 *   `maxStudentsPerSection` per remaining section.
 * - `leave-last-sl`: the SL is the only one in a slot with students.
 * - `join-capped`: phase 1, the slot is at its `slSlotCaps` entry.
 * - `join-under-min`: phase 2, the slot has spare seats and its
 *   projected students would not reach `minStudentsPerSection`
 *   per section with one more section.
 */
export type SlGateRefusal = z.infer<typeof SlGateRefusalSchema>;

/** Occupancy of one slot in one pool, as the gate sees it. */
export interface SlGateSlotState {
  /** Countable SL sections in the slot. */
  sections: number;
  /** Students who have picked the slot. */
  students: number;
  /** `sections × capStudents − students`; negative when oversubscribed. */
  remainingSeats: number;
}

/** Result of a gate test. */
export interface SlGateVerdict {
  /** Whether the move may be written. */
  allowed: boolean;
  /** Set exactly when `allowed` is `false`. */
  reason?: SlGateRefusal;
}

const ALLOWED: SlGateVerdict = {allowed: true};

/** Inputs to {@link evaluateSlJoin}. */
export interface SlGateJoinInput {
  /** Slot being joined, counted without the joining SL. */
  to: SlGateSlotState;
  /** Time index of `to`, used to look up its cap. */
  timeIndex: number;
  /** Per-slot SL caps keyed by `String(timeIndex)`. */
  slSlotCaps: Readonly<SectionSignupV2SlSlotCaps>;
  /** Smallest acceptable students per section. */
  minStudentsPerSection: number;
  /**
   * Expected student picks at close, or `null` when not configured.
   * See {@link isSlGatePhaseTwo} for how each value gates phase 2.
   */
  expectedStudents: number | null;
  /** Student picks so far in the pool. */
  picksSoFar: number;
  /** Fraction of `expectedStudents` at which phase 2 starts. */
  liveMinAt: number;
  /**
   * Earliest time phase 2 may start, in epoch ms, or `null` when not
   * configured. See {@link isSlGatePhaseTwo}.
   */
  phaseTwoNotBeforeMs: number | null;
  /** Current time in epoch ms. */
  nowMs: number;
  /** The SL already sits in `to`; re-picking always passes. */
  isRePick: boolean;
  /**
   * Whether a slot at its cap also refuses joins in phase 2. Set for a
   * course whose student picks may exceed seats, where the phase-2
   * allowances hold for any slot with demand. When omitted, `false`.
   */
  keepsCapsInPhaseTwo?: boolean;
  /**
   * Historical share of the pool's picks by `timeIndex`, used for the
   * picks still to come. When omitted, uses
   * `SECTION_SIGNUP_V2_MEAN_SHARE_BY_SLOT`.
   */
  meanShareBySlot?: Readonly<Record<number, number>>;
}

/**
 * Projects a slot's picks at close: the picks it already has, plus the
 * pool's picks still to come split by the slot's historical share.
 *
 * The picks still to come are not scaled from the slot's live share
 * because early pickers come mostly from the time zones awake when
 * invitations go out, so the live share misjudges slots that suit the
 * other time zones.
 *
 * @param options.students - Current picks in the slot.
 * @param options.expectedStudents - Expected picks in the pool at
 *   close, or `null` when not configured.
 * @param options.picksSoFar - Current picks in the pool.
 * @param options.meanShare - The slot's historical share of the pool's
 *   picks, in `[0, 1]`.
 * @return `students + max(0, expectedStudents − picksSoFar) × meanShare`,
 *   or `students` unchanged when `expectedStudents` is `null`. Never
 *   below `students`.
 * @throws Never.
 */
export function projectedStudents({
  students,
  expectedStudents,
  picksSoFar,
  meanShare,
}: {
  students: number;
  expectedStudents: number | null;
  picksSoFar: number;
  meanShare: number;
}): number {
  if (expectedStudents === null) {
    return students;
  }
  return students + Math.max(0, expectedStudents - picksSoFar) * meanShare;
}

/**
 * Looks up a slot's historical share of a pool's picks.
 *
 * @param options.timeIndex - The slot.
 * @param options.meanShareBySlot - Shares by `timeIndex`. When omitted,
 *   uses `SECTION_SIGNUP_V2_MEAN_SHARE_BY_SLOT`.
 * @return The slot's share, or `0` for a slot with no entry.
 * @throws Never.
 */
export function slGateMeanShare({
  timeIndex,
  meanShareBySlot = SECTION_SIGNUP_V2_MEAN_SHARE_BY_SLOT,
}: {
  timeIndex: number;
  meanShareBySlot?: Readonly<Record<number, number>>;
}): number {
  return Object.hasOwn(meanShareBySlot, timeIndex)
    ? meanShareBySlot[timeIndex]
    : 0;
}

/**
 * Projects an availability row onto the counts the gate reads.
 *
 * @param options.availabilitySlot - The slot's row from
 *   `computeSectionSignupV2Availability`. Omitted or `undefined`
 *   means the slot has no row (no SLs and no students), which is
 *   all zeros.
 * @return The slot's gate state.
 * @throws Never.
 */
export function slGateSlotState({
  availabilitySlot,
}: {
  availabilitySlot?: SectionSignupV2SlotAvailability;
}): SlGateSlotState {
  if (availabilitySlot === undefined) {
    return {sections: 0, students: 0, remainingSeats: 0};
  }
  return {
    sections: availabilitySlot.numSections,
    students: availabilitySlot.numStudents,
    remainingSeats: availabilitySlot.remainingStudentSpots,
  };
}

/**
 * Leave test: whether an SL may leave slot `from`, counted with
 * the leaving SL.
 *
 * With one section, leaving is allowed iff the slot has no
 * students. With more, it is allowed iff
 * `students / (sections − 1) ≤ maxStudentsPerSection`; the bound is
 * inclusive, so leaving exactly at max is allowed.
 *
 * @param options.from - Slot being left, including the leaving SL.
 * @param options.maxStudentsPerSection - Largest acceptable
 *   students per section.
 * @return The verdict.
 * @throws Never.
 */
export function evaluateSlLeave({
  from,
  maxStudentsPerSection,
}: {
  from: SlGateSlotState;
  maxStudentsPerSection: number;
}): SlGateVerdict {
  if (from.sections <= 1) {
    return from.students === 0
      ? ALLOWED
      : {allowed: false, reason: 'leave-last-sl'};
  }
  return from.students <= maxStudentsPerSection * (from.sections - 1)
    ? ALLOWED
    : {allowed: false, reason: 'leave-over-max'};
}

/**
 * Whether the pool is in phase 2 of the join test. Phase 2 requires
 * a configured, positive `expectedStudents` and a configured
 * `phaseTwoNotBeforeMs`, and is then
 * `picksSoFar ≥ liveMinAt × expectedStudents` and
 * `nowMs ≥ phaseTwoNotBeforeMs`. Both bounds are inclusive, so a pool
 * exactly at either threshold is in phase 2.
 *
 * `null` for either setting (not configured) stays in phase 1
 * regardless of picks. A configured `0` expected also stays in phase
 * 1: the threshold would be `0`, putting the pool in phase 2 before any
 * student picks.
 *
 * @param options.expectedStudents - Expected picks in the pool, or
 *   `null` when not configured.
 * @param options.picksSoFar - Current picks in the pool.
 * @param options.liveMinAt - Fraction of expected picks at which
 *   phase 2 starts.
 * @param options.phaseTwoNotBeforeMs - Earliest time phase 2 may
 *   start, in epoch ms, or `null` when not configured.
 * @param options.nowMs - Current time in epoch ms.
 * @return `true` in phase 2.
 * @throws Never.
 */
export function isSlGatePhaseTwo({
  expectedStudents,
  picksSoFar,
  liveMinAt,
  phaseTwoNotBeforeMs,
  nowMs,
}: {
  expectedStudents: number | null;
  picksSoFar: number;
  liveMinAt: number;
  phaseTwoNotBeforeMs: number | null;
  nowMs: number;
}): boolean {
  return (
    expectedStudents !== null &&
    expectedStudents > 0 &&
    picksSoFar >= liveMinAt * expectedStudents &&
    phaseTwoNotBeforeMs !== null &&
    nowMs >= phaseTwoNotBeforeMs
  );
}

/**
 * Join test: whether an SL may add a section to slot `to`.
 *
 * Phase 1 (see {@link isSlGatePhaseTwo}): allowed iff the slot has
 * no cap or `sections + 1 ≤ cap` (inclusive, so the join that
 * reaches the cap is allowed).
 *
 * Phase 2: allowed iff any of
 * - the slot has a cap and `sections + 1 ≤ cap`;
 * - `sections == 0` (uncovered, demand unobservable);
 * - `remainingSeats ≤ 0` (full, demand beyond seats unobservable);
 * - `projectedStudents / (sections + 1) ≥ minStudentsPerSection`
 *   (inclusive, so exactly at min is allowed).
 *
 * With `keepsCapsInPhaseTwo`, a capped slot is refused in phase 2 exactly
 * as in phase 1; a slot with no cap keeps the phase-2 test.
 *
 * A re-pick passes both phases.
 *
 * @param input - See {@link SlGateJoinInput}.
 * @return The verdict.
 * @throws Never.
 */
export function evaluateSlJoin({
  to,
  timeIndex,
  slSlotCaps,
  minStudentsPerSection,
  expectedStudents,
  picksSoFar,
  liveMinAt,
  phaseTwoNotBeforeMs,
  nowMs,
  isRePick,
  keepsCapsInPhaseTwo = false,
  meanShareBySlot,
}: SlGateJoinInput): SlGateVerdict {
  if (isRePick) {
    return ALLOWED;
  }
  const capKey = String(timeIndex);
  const cap: number | undefined = Object.hasOwn(slSlotCaps, capKey)
    ? slSlotCaps[capKey]
    : undefined;
  const isUnderCap = cap !== undefined && to.sections + 1 <= cap;
  const isAtCap = cap !== undefined && !isUnderCap;
  const isPhaseTwo = isSlGatePhaseTwo({
    expectedStudents,
    picksSoFar,
    liveMinAt,
    phaseTwoNotBeforeMs,
    nowMs,
  });

  if (isAtCap && (!isPhaseTwo || keepsCapsInPhaseTwo)) {
    return {allowed: false, reason: 'join-capped'};
  }
  if (
    !isPhaseTwo ||
    isUnderCap ||
    to.sections === 0 ||
    to.remainingSeats <= 0
  ) {
    return ALLOWED;
  }
  const projected = projectedStudents({
    students: to.students,
    expectedStudents,
    picksSoFar,
    meanShare: slGateMeanShare({timeIndex, meanShareBySlot}),
  });
  return projected >= minStudentsPerSection * (to.sections + 1)
    ? ALLOWED
    : {allowed: false, reason: 'join-under-min'};
}

/** Inputs to {@link evaluateSlMove}. */
export type SlGateMoveInput = Omit<SlGateJoinInput, 'to' | 'timeIndex'> & {
  /** Slot being left, including the SL; omitted on a first pick. */
  from?: SlGateSlotState;
  /** Slot being joined; omitted when choosing `'none'`. */
  to?: SlGateSlotState;
  /** Time index of `to`; required when `to` is given. */
  timeIndex?: number;
  /** Largest acceptable students per section, for the leave test. */
  maxStudentsPerSection: number;
};

/**
 * Runs the leave test on `from` (if given), then the join test on
 * `to` (if given), and returns the first refusal. A re-pick is not
 * a move and passes without either test.
 *
 * @param input - See {@link SlGateMoveInput}.
 * @return The verdict.
 * @throws {Error} When `to` is given without `timeIndex`.
 */
export function evaluateSlMove({
  from,
  to,
  timeIndex,
  maxStudentsPerSection,
  ...join
}: SlGateMoveInput): SlGateVerdict {
  if (join.isRePick) {
    return ALLOWED;
  }
  if (from !== undefined) {
    const leave = evaluateSlLeave({from, maxStudentsPerSection});
    if (!leave.allowed) {
      return leave;
    }
  }
  if (to === undefined) {
    return ALLOWED;
  }
  if (timeIndex === undefined) {
    throw new Error('evaluateSlMove: timeIndex is required when to is given');
  }
  return evaluateSlJoin({...join, to, timeIndex});
}
